import React, { useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { chipLabel, dropStale, type GroupByField } from './groupByModel'
import { GroupByPane } from './GroupByPane'

export interface GroupBySettingsProps {
  fields: GroupByField[]
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  onOpen?: () => void
}

const chipSx = {
  textTransform: 'none' as const,
  fontSize: 12,
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export function GroupBySettings({
  fields,
  value,
  onChange,
  disabled = false,
  onOpen,
}: GroupBySettingsProps) {
  const fieldNames = useMemo(() => fields.map((f) => f.name), [fields])
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>([])

  const openDialog = () => {
    if (disabled) return
    setDraft(dropStale(value, fieldNames))
    setOpen(true)
  }

  const closeWithoutCommit = () => setOpen(false)

  const apply = () => {
    onChange([...draft])
    setOpen(false)
  }

  if (onOpen) {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={disabled}
        aria-label="Group by"
        title={chipLabel(value, fields)}
        onClick={() => {
          if (!disabled) onOpen()
        }}
        sx={chipSx}
      >
        {chipLabel(value, fields)}
      </Button>
    )
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        disabled={disabled}
        aria-label="Group by"
        title={chipLabel(value, fields)}
        onClick={openDialog}
        sx={chipSx}
      >
        {chipLabel(value, fields)}
      </Button>
      <Dialog
        open={open}
        onClose={closeWithoutCommit}
        maxWidth="md"
        fullWidth
        transitionDuration={0}
        aria-labelledby="group-by-settings-title"
      >
        <DialogTitle id="group-by-settings-title">Grouping</DialogTitle>
        <DialogContent>
          <GroupByPane fields={fields} draft={draft} onDraftChange={setDraft} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWithoutCommit}>Cancel</Button>
          <Button variant="contained" onClick={apply}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
